<template>
    <div>
        <button type="submit" class="btn btn-primary" @click="viewPDF">
            <i v-if="isLoading" class="fas fa-spinner fa-pulse"></i>
            PDF
        </button>
    </div>
</template>

<script>
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import moment from 'moment-timezone'
import Common from '../../../../common/Common.vue'

export default {
    props: {
        jsonData: {
            type: Array,
            default: () => [],
        },
        pdfHeader: {
            type: Array,
            default: () => [],
        },
        jsonTitle: {
            type: String,
            default: '',
        },
        filename: {
            type: String,
            default: '',
        },
        logoURL: {
            type: String,
            default: '',
        },
        startDate: {
            // Accept either a string or a Date/Moment object
            type: [String, Object],
            default: null,
        },
        endDate: {
            type: [String, Object],
            default: null,
        },
    },
    watch: {
        jsonData() {
            this.data = this.jsonData
            this.title = this.jsonTitle
            this.headers = this.pdfHeader
            this.name = this.filename + '.pdf'
        },
    },
    data() {
        return {
            isLoading: false,
            data: [],
            title: '',
            headers: [],
            name: '',
        }
    },
    methods: {
        translate(key, fallback) {
            try {
                if (!this.$t) return fallback
                const translated = this.$t(key)
                if (!translated || String(translated).includes(key)) return fallback
                return translated
            } catch (e) {
                return fallback
            }
        },
        async downloadPDF() {
            this.isLoading = true
            const accountTimezone = Common.getAccountTimezone()
            const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' })
            const pageWidth = doc.internal.pageSize.getWidth()
            const margin = 15
            let y = margin

            // Adicionar logo se existir (centralizado)
            if (this.logoURL) {
                try {
                    const imgData = await this.loadImageWithWhiteBackground(this.logoURL)
                    const imgProps = doc.getImageProperties(imgData)
                    const imgWidth = 40 // Largura maior para destaque
                    const imgHeight = (imgProps.height * imgWidth) / imgProps.width
                    const x = (pageWidth - imgWidth) / 2
                    doc.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight)
                    y += imgHeight + 6
                } catch (e) {
                    // ignore image errors and continue
                }
            }

            // Título (compacto) e metadados alinhados: título centralizado pequeno e metadados à direita
            doc.setFont('helvetica', 'bold')
            doc.setFontSize(13)
            const titleText = this.title ? String(this.title).toUpperCase() : ''
            doc.text(titleText, pageWidth / 2, y, { align: 'center' })
            y += 7

            // Metadados à direita (Gerado em / Período), mais compactos
            doc.setFontSize(9)
            doc.setFont('helvetica', 'normal')
            const generatedLabel = this.$t ? this.$t('str.generated') : 'Gerado em:'
            const generatedText = `${generatedLabel} ${moment().tz(accountTimezone).format('DD/MM/YYYY HH:mm:ss')}`
            const startDate = this?.startDate ? moment.tz(this.startDate, accountTimezone).format('DD/MM/YYYY') : ''
            const endDate = this?.endDate ? moment.tz(this.endDate, accountTimezone).format('DD/MM/YYYY') : ''
            const periodText = (startDate || endDate) ? `${this.$t ? this.$t('str.period') : 'Período'}: ${startDate} - ${endDate}` : ''

            // Right column for metadata
            const rightX = pageWidth - margin
            doc.text(generatedText, rightX, y, { align: 'right' })
            if (periodText) {
                y += 5
                doc.text(periodText, rightX, y, { align: 'right' })
            }
            y += 8

            // Normalize source data into flat rows: { account, client, site, point, date, scans }
            let rows = []
            if (Array.isArray(this.data) && this.data.length > 0) {
                if (this.data[0].items) {
                    // data is already sectioned: each section.items is an array of rows
                    this.data.forEach(section => {
                        const items = section.items || []
                        items.forEach(i => rows.push(i))
                    })
                } else {
                    rows = [...this.data]
                }
            }

            // Group rows into account -> client -> site -> [rows]
            // We intentionally stop grouping at site level so PDF groups up to Local/Site
            const grouped = {}
            rows.forEach(r => {
                const acc = r.account || ' '
                const cli = r.client || ' '
                const sit = r.site || ' '

                grouped[acc] = grouped[acc] || {}
                grouped[acc][cli] = grouped[acc][cli] || {}
                grouped[acc][cli][sit] = grouped[acc][cli][sit] || []
                grouped[acc][cli][sit].push(r)
            })

            // If no grouped data, render a centered "no records" message and finish
            if (Object.keys(grouped).length === 0) {
                doc.setFontSize(11)
                doc.setFont('helvetica', 'normal')
                const noRecordsText = this.translate('str.noRecordsForPeriod', 'Sem registros para o período.')
                doc.text(noRecordsText, pageWidth / 2, y, { align: 'center' })
                this.isLoading = false
                return doc.output('blob')
            }

            // Determine if this is the 'flat tabular' report (contains plannedDate/readingTime)
            const isTabularReport = this.headers && this.headers.some(h => ['plannedDate', 'readingTime', 'status', 'point', 'site'].includes(h.datakey))

            // Determine column titles from provided headers (fallbacks for legacy grouped reports)
            const dateTitle = (this.headers && this.headers.find(h => h.datakey === 'date') && this.headers.find(h => h.datakey === 'date').title) || this.translate('str.table.reports.column.date', 'Data')
            const scansTitle = (this.headers && this.headers.find(h => h.datakey === 'scans') && this.headers.find(h => h.datakey === 'scans').title) || this.translate('str.table.reports.column.scan.readings', 'Leituras')

            // Iterate accounts with compact, consistent left alignment
            const baseX = margin
            const indentClient = baseX
            const indentSite = baseX

            let firstAccount = true
            for (const acc of Object.keys(grouped)) {
                if (!firstAccount) {
                    doc.addPage()
                    y = margin
                }
                firstAccount = false

                // Account header (prominent) - left aligned consistently
                doc.setFontSize(11)
                doc.setFont('helvetica', 'bold')
                doc.text(acc ? String(acc).toUpperCase() : ' ', baseX, y)
                y += 4

                const clients = grouped[acc]
                for (const cli of Object.keys(clients)) {
                    doc.setFontSize(9)
                    doc.setFont('helvetica', 'normal')
                    doc.text(cli ? String(cli) : ' ', indentClient, y)
                    y += 3

                    const sites = clients[cli]
                    for (const sit of Object.keys(sites)) {
                        // Site name should be left aligned at same X and sit on the line where table starts
                        doc.setFontSize(9)
                        doc.setFont('helvetica', '600')
                        doc.text(sit ? String(sit) : ' ', indentSite, y)
                        // leave a clear gap before the first point to avoid touching the site name
                        y += 6

                        const siteRows = sites[sit] || []

                        // Add a small spacer so site isn't touching the previous text
                        y += 2

                        // If this is a tabular report (plannedDate/readingTime etc), render a single table per site
                        if (isTabularReport) {
                            // Remove grouping columns from table if they are already present as headers outside the table
                            const headers = (this.headers || []).filter(h => !['account', 'client', 'site'].includes(h.datakey))
                            // Fallback to original headers if filtering removed everything
                            const effectiveHeaders = headers.length > 0 ? headers : (this.headers || [])
                            const headTitles = effectiveHeaders.map(h => h.title)

                            const body = siteRows.map(r => {
                                return effectiveHeaders.map(h => {
                                    const key = h.datakey
                                    let value = r[key]
                                    if (key === 'plannedDate' && value) {
                                        try {
                                            if (/^\d{4}-\d{2}-\d{2}T/.test(value) || /^\d{4}-\d{2}-\d{2}$/.test(value)) {
                                                return moment.tz(value, accountTimezone).format('DD/MM/YYYY')
                                            }
                                        } catch (e) {
                                            // ignore formatting errors
                                        }
                                        return value
                                    }
                                    if (key === 'readingTime' && value) return value
                                    if (key === 'status' && value) return value
                                    if (key === 'point' && value) return value
                                    if (key === 'site' && value) return value
                                    return value !== undefined && value !== null ? String(value) : ''
                                })
                            })

                            if (body.length === 0) body.push(effectiveHeaders.map(() => '-'))

                            doc.autoTable({
                                startY: y,
                                head: [headTitles],
                                body,
                                theme: 'striped',
                                styles: { font: 'helvetica', fontSize: 9, cellPadding: 2 },
                                headStyles: { fillColor: [90, 90, 90], textColor: 255, fontStyle: 'bold' },
                                didDrawPage: dataArg => {
                                    y = dataArg.cursor.y + 4
                                },
                            })

                            y += 3
                        } else {
                            // Legacy fallback: render simple two-column table aggregating by date
                            const tableBody = siteRows.map(r => {
                                const dateLabel = r.date || r.plannedDate || ''
                                const scansStr = r.scans || ''
                                let dateFormatted = dateLabel
                                if (/^\d{4}-\d{2}-\d{2}$/.test(dateLabel)) dateFormatted = moment(dateLabel).format('DD/MM/YYYY')
                                return [dateFormatted, scansStr || ' ']
                            })

                            if (tableBody.length === 0) tableBody.push(['-', this.translate('str.noData', 'Sem registros')])

                            doc.autoTable({
                                startY: y,
                                head: [[dateTitle, scansTitle]],
                                body: tableBody,
                                theme: 'striped',
                                styles: { font: 'helvetica', fontSize: 8.5, cellPadding: 1.5 },
                                headStyles: { fillColor: [90, 90, 90], textColor: 255, fontStyle: 'bold' },
                                columnStyles: {
                                    0: { cellWidth: 38 },
                                    1: { cellWidth: 'auto' },
                                },
                                didDrawPage: dataArg => {
                                    y = dataArg.cursor.y + 4
                                },
                            })

                            y += 3
                        }
                    }
                }
            }

            this.isLoading = false
            return doc.output('blob')
        },
        async viewPDF() {
            try {
                const pdfData = await this.downloadPDF()
                const pdfURL = URL.createObjectURL(pdfData)
                window.open(pdfURL, '_blank')
            } catch {
                this.isLoading = false
            }
        },
        async loadImageWithWhiteBackground(url) {
            return new Promise((resolve, reject) => {
                const img = new Image()
                img.crossOrigin = 'Anonymous'
                img.onload = () => {
                    const canvas = document.createElement('canvas')
                    const ctx = canvas.getContext('2d')
                    canvas.width = img.width
                    canvas.height = img.height

                    // Preencher o fundo com branco
                    ctx.fillStyle = '#FFFFFF'
                    ctx.fillRect(0, 0, canvas.width, canvas.height)

                    // Desenhar a imagem sobre o fundo branco
                    ctx.drawImage(img, 0, 0)
                    resolve(canvas.toDataURL('image/png'))
                }
                img.onerror = () => reject(new Error('Failed to load image'))
                img.src = url
            })
        },
    },
}
</script>
